import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterRondebosch() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-rondebosch",
    name: "Painters Rondebosch",
    location: "Rondebosch, Cape Town",
    rating: 4.8,
    reviews: 181,
    specialties: ["Interior", "Exterior", "Heritage Homes", "University Properties"],
    image: "https://public.readdy.ai/ai/img_res/d27de417d1418b1c044e6579770b9919.jpg",
    verified: true,
    yearsExperience: 14,
    website: "https://paintersrondebosch.co.za",
    email: "info@paintersrondebosch.co.za",
    description: "Expert painting services in Rondebosch and southern suburbs. Specializing in heritage home restoration, university properties, and modern homes with architectural sensitivity.",
    services: [
      "Heritage Home Restoration",
      "Victorian Property Painting", 
      "University Property Maintenance",
      "Student Accommodation Painting",
      "Academic Building Painting",
      "Modern Home Interior Painting",
      "Period Feature Restoration",
      "Architectural Detail Preservation"
    ],
    serviceAreas: ["Rondebosch", "Newlands", "Claremont", "Mowbray", "Southern Suburbs", "UCT", "Cape Town", "Western Cape"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20heritage%20home%20interior%20painted%20by%20professional%20painters%20in%20Rondebosch%2C%20Victorian%20architecture%20with%20fresh%20painted%20walls%2C%20quality%20paint%20finish%2C%20mountain%20views%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work&width=400&height=300&seq=rondebosch-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=University%20building%20painting%20project%20in%20Rondebosch%20Cape%20Town%2C%20academic%20facility%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20exterior%20finish%2C%20campus%20setting%20with%20trees%2C%20well-maintained%20property&width=400&height=300&seq=rondebosch-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Heritage%20property%20restoration%20in%20Rondebosch%2C%20professional%20painters%20working%20on%20Victorian%20home%20exterior%2C%20quality%20residential%20paint%20finish%2C%20tree-lined%20street%2C%20organized%20work%20site&width=400&height=300&seq=rondebosch-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Student%20accommodation%20painting%20in%20Rondebosch%2C%20fresh%20painted%20residential%20building%2C%20quality%20paint%20finish%2C%20bright%20modern%20student%20housing%2C%20professional%20painting%20work&width=400&height=300&seq=rondebosch-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Professor Elizabeth Carter",
        rating: 5,
        comment: "Outstanding work on our heritage home in Rondebosch. The painters understood the historical significance and delivered a beautiful restoration that preserves the original character while providing modern protection."
      },
      {
        name: "Dr. Michael Thompson", 
        rating: 5,
        comment: "Professional service from start to finish. They completed our university building painting on schedule and the quality exceeded our expectations. Excellent understanding of academic environment requirements."
      },
      {
        name: "Sarah Williams",
        rating: 4,
        comment: "Great attention to detail and very respectful of our Victorian property. The team was knowledgeable about heritage conservation and the results are magnificent."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Rondebosch | Professional House Painting Services Cape Town | Residential Painters";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Rondebosch provides professional painting services in Rondebosch, Cape Town. Specializing in residential painting, heritage properties, interior and exterior work. Quality local painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Rondebosch, house painters Rondebosch, painting contractors Cape Town, residential painting, heritage property painting');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20heritage%20home%20in%20Rondebosch%20Cape%20Town%2C%20tree-lined%20suburban%20street%20with%20mountain%20views%2C%20painters%20in%20clean%20uniforms%20with%20quality%20brushes%20and%20equipment%2C%20well-maintained%20Victorian%20and%20Edwardian%20architecture%2C%20bright%20natural%20lighting%2C%20high%20quality%20heritage%20painting%20restoration%20project&width=1200&height=600&seq=rondebosch-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Rondebosch - Your Heritage and University Property Specialists</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description} With over 14 years of specialized experience in the prestigious Rondebosch area, we have established ourselves as the premier choice for heritage property restoration, university facility maintenance, and discerning homeowners who value both architectural sensitivity and exceptional craftsmanship.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Heritage Home Restoration Specialists</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Rondebosch is home to some of Cape Town's most magnificent heritage properties, including stunning Victorian and Edwardian homes that require specialized restoration techniques and deep understanding of historical conservation principles. Our team has extensive experience working with heritage consultants, local authorities, and conservation specialists to ensure all restoration work meets strict heritage guidelines while incorporating modern protective technologies that extend the life of these architectural treasures. We understand the importance of preserving original features while providing contemporary protection against environmental factors.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">University of Cape Town Property Expertise</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  As the home of the University of Cape Town, Rondebosch hosts numerous academic buildings, student accommodations, and university facilities that require specialized painting services understanding the unique demands of educational environments. Our team has extensive experience working within university settings, coordinating with academic schedules, managing projects during term breaks, and ensuring minimal disruption to educational activities. We understand the importance of creating inspiring learning environments while meeting the practical durability requirements of high-traffic academic facilities.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Student Accommodation and Rental Property Services</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The proximity to UCT means Rondebosch has a significant number of student accommodations and rental properties that require efficient, cost-effective painting solutions balancing aesthetic appeal with practical durability. Our team understands the unique challenges of rental property maintenance, including quick turnaround times between tenants, budget considerations, and the need for durable finishes that withstand heavy use. We work closely with property managers and landlords to provide solutions that maintain property values while managing costs effectively.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Architectural Detail Preservation</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Many Rondebosch properties feature intricate architectural details, period moldings, and decorative elements that require specialized restoration techniques and careful attention to historical accuracy. Our team has specialized training in traditional painting techniques, period-appropriate color schemes, and the delicate work required to restore and preserve architectural details without compromising their historical integrity. We use traditional materials and methods where appropriate while incorporating modern protective elements that ensure long-term preservation.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Modern Home Integration</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  While heritage properties are prominent in Rondebosch, the area also features contemporary homes and modern renovations that require different approaches balancing respect for the neighborhood's character with contemporary design preferences. Our team excels at creating cohesive color schemes and finishes that allow modern homes to complement the area's heritage character while expressing individual style preferences. We understand how to work with contemporary materials and design elements while maintaining sensitivity to the broader architectural context of this prestigious suburb.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20heritage%20home%20in%20Rondebosch%20Cape%20Town%2C%20southern%20suburbs%20property%20near%20university%20with%20mountain%20views%2C%20painters%20in%20uniform%20with%20quality%20equipment%2C%20traditional%20Victorian%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=rondebosch-sidebar-image&orientation=landscape"
                      alt="Professional painters Rondebosch heritage house painting services Cape Town southern suburbs university area"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Comprehensive Heritage and University Property Painting Services</h2>
              
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Specialized Rondebosch Painting Solutions</h3>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Our comprehensive painting services are specifically designed for the unique requirements of heritage properties, university facilities, and modern homes throughout Rondebosch and the southern suburbs. From Victorian mansion restoration to student accommodation maintenance and contemporary home enhancement, we provide specialized solutions that honor architectural heritage while meeting modern performance standards.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} services with heritage-appropriate materials and expert Rondebosch application techniques.</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Heritage Property Restoration</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our heritage property restoration services combine traditional techniques with modern protective technologies to preserve and enhance Rondebosch's magnificent historical buildings. We work closely with heritage consultants to ensure all restoration work meets conservation standards while providing long-lasting protection against environmental factors.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our team has specialized training in traditional painting techniques, period-appropriate color schemes, and the delicate work required to restore architectural details without compromising historical integrity. We understand the importance of preserving original features while incorporating modern protective elements that ensure long-term preservation.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">University Property Maintenance</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our university property maintenance services are designed to meet the unique demands of academic environments, including coordination with academic schedules, managing high-traffic areas, and creating inspiring learning environments that support educational excellence while meeting practical durability requirements.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We understand the importance of minimizing disruption to educational activities while delivering professional results that enhance the university environment. Our team is experienced in working within institutional settings, following safety protocols, and coordinating with facility management to ensure seamless project completion.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Recent Heritage and University Projects in Rondebosch</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Explore our portfolio of recent painting projects throughout Rondebosch and the southern suburbs. Each project demonstrates our expertise in heritage restoration, university facility maintenance, and modern property enhancement with attention to architectural sensitivity and quality craftsmanship.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Rondebosch Project {index + 1}</h3>
                      <p className="text-gray-600 text-sm">Professional heritage and university property painting with specialized materials and expert restoration techniques.</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews and Testimonials</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Read what our satisfied customers throughout Rondebosch and the southern suburbs have to say about our specialized painting services. Our commitment to heritage preservation and university property excellence is reflected in these genuine reviews from homeowners, property managers, and university administrators.
              </p>
              
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Painters Rondebosch</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Ready to enhance your Rondebosch heritage property or university facility with professional painting services? Contact us today for a free consultation and quote. We're here to help you achieve beautiful, lasting results that honor architectural heritage while meeting modern performance standards.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding southern suburbs</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Heritage Assessment</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Request a free, no-obligation assessment for your Rondebosch heritage property or university facility painting project. Our experienced team will evaluate your property's specific conservation requirements and provide detailed recommendations.
                </p>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
